import { useState, useMemo, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { ChevronRight, CheckCircle, Send, FileText, Zap, Info } from "lucide-react";
import axios from "axios";
import { iphoneData, macData, ipadData, watchData } from "../data/repairData";
import HCaptchaField from "../components/HCaptchaField";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Data mapping ────────────────────────────────────────────────────
const DATA_MAP = {
  iPhone: { data: iphoneData, gammeFilter: null },
  "MacBook Air": { data: macData, gammeFilter: "MacBook Air" },
  "MacBook Pro": { data: macData, gammeFilter: "MacBook Pro" },
  iMac: { data: macData, gammeFilter: "iMac" },
  iPad: { data: ipadData, gammeFilter: null },
  "Apple Watch": { data: watchData, gammeFilter: null },
};

const REPAIR_FIELD = {
  "Remplacement écran": "ecran",
  "Remplacement batterie": "batterie",
  "Remplacement clavier": "clavier",
  "Vitre arrière": "vitre_arriere",
  "Remplacement vitre tactile": "vitre",
  "Remplacement écran LCD": "ecran",
};

function getGammes(appareil) {
  const entry = DATA_MAP[appareil];
  if (!entry) return [];
  const { data, gammeFilter } = entry;
  return gammeFilter
    ? data.gammes.filter((g) => g.name.toLowerCase().includes(gammeFilter.toLowerCase()))
    : data.gammes;
}

function getEstimation(appareil, modele, typeReparation) {
  const gammes = getGammes(appareil);
  const allModels = gammes.flatMap((g) => g.models);
  const found = allModels.find((m) => m.model === modele);
  if (!found) return null;
  const field = REPAIR_FIELD[typeReparation];
  if (!field || found[field] === undefined) return "Sur devis";
  return found[field]; // number or "Sur devis"
}

// ─── Static data ─────────────────────────────────────────────────────
const appareils = ["iPhone", "MacBook Air", "MacBook Pro", "iMac", "iPad", "Apple Watch", "Autre"];
const reparations = {
  iPhone: ["Remplacement écran", "Remplacement batterie", "Connecteur de charge", "Caméra", "Vitre arrière", "Face ID / Touch ID", "Autre"],
  "MacBook Air": ["Remplacement écran", "Remplacement batterie", "Remplacement clavier", "Réparation SSD", "Réparation carte mère", "Trackpad", "Autre"],
  "MacBook Pro": ["Remplacement écran", "Remplacement batterie", "Remplacement clavier", "Réparation SSD", "Réparation carte mère", "Trackpad", "Autre"],
  iMac: ["Remplacement écran", "Mise à jour SSD", "Réparation carte mère", "Nettoyage", "Autre"],
  iPad: ["Remplacement vitre tactile", "Remplacement écran LCD", "Remplacement batterie", "Connecteur de charge", "Autre"],
  "Apple Watch": ["Remplacement écran", "Remplacement batterie", "Diagnostic", "Autre"],
  Autre: ["Réparation", "Diagnostic", "Autre"],
};

const inputCls = "w-full bg-[#1A1A1E] border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#FF5C00] transition-colors text-sm";
const labelCls = "block text-xs font-medium text-white/50 mb-1.5";

// ─── Price badge ──────────────────────────────────────────────────────
function PriceEstimation({ estimation }) {
  if (estimation === null) return null;

  const isNumber = typeof estimation === "number";

  return (
    <div
      data-testid="price-estimation"
      className={`p-5 rounded-2xl border transition-all duration-300 ${
        isNumber
          ? "border-[#FF5C00]/40 bg-gradient-to-br from-[#FF5C00]/10 to-[#FF5C00]/5"
          : "border-white/10 bg-[#1A1A1E]"
      }`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Zap className={`w-4 h-4 ${isNumber ? "text-[#FF5C00]" : "text-white/40"}`} />
        <span className="text-xs font-bold uppercase tracking-widest text-white/40">
          Estimation instantanée
        </span>
      </div>

      {isNumber ? (
        <>
          <div className="flex items-baseline gap-1 mb-1">
            <span className="text-4xl font-black text-white">{estimation}</span>
            <span className="text-xl font-bold text-[#FF5C00]">€</span>
          </div>
          <p className="text-xs text-white/40 leading-relaxed flex items-start gap-1.5">
            <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
            Tarif indicatif. Confirmé après diagnostic gratuit sur place.
          </p>
        </>
      ) : (
        <>
          <p className="text-xl font-black text-white/60 mb-1">Sur devis</p>
          <p className="text-xs text-white/40 leading-relaxed">
            Ce type de réparation nécessite un diagnostic. Apportez votre appareil ou envoyez ce formulaire.
          </p>
        </>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────
export default function DevisPage() {
  useEffect(() => { document.title = "Devis Gratuit — Atelier du Mac Paris"; }, []);
  const [form, setForm] = useState({
    nom: "", email: "", telephone: "",
    appareil: "iPhone", modele: "",
    type_reparation: "Remplacement écran",
    description: "", disponibilite: "",
  });
  const [status, setStatus] = useState(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaRef = useRef(null);

  // Derived: gammes + all models for current appareil
  const gammes = useMemo(() => getGammes(form.appareil), [form.appareil]);
  const hasModels = gammes.length > 0;

  // Real-time price estimation
  const estimation = useMemo(
    () => (form.modele ? getEstimation(form.appareil, form.modele, form.type_reparation) : null),
    [form.appareil, form.modele, form.type_reparation]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "appareil") {
        updated.modele = "";
        updated.type_reparation = reparations[value]?.[0] || "Autre";
      }
      return updated;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!captchaToken) { setStatus("captcha"); return; }
    setStatus("loading");
    try {
      await axios.post(`${BACKEND_URL}/api/devis`, { ...form, hcaptcha_token: captchaToken });
      setStatus("success");
      setForm({ nom: "", email: "", telephone: "", appareil: "iPhone", modele: "", type_reparation: "Remplacement écran", description: "", disponibilite: "" });
      setCaptchaToken("");
      captchaRef.current?.resetCaptcha();
    } catch {
      setStatus("error");
      captchaRef.current?.resetCaptcha();
      setCaptchaToken("");
    }
  };

  return (
    <div className="bg-[#0A0A0C] min-h-screen">
      {/* Header */}
      <section className="pt-32 pb-8 max-w-4xl mx-auto px-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-white/40 hover:text-white/60 mb-8 transition-colors">
          <ChevronRight className="w-3 h-3 rotate-180" />
          Retour à l'accueil
        </Link>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-[#FF5C00]/10 flex items-center justify-center">
            <FileText className="w-6 h-6 text-[#FF5C00]" />
          </div>
          <span className="text-xs font-bold uppercase tracking-widest text-[#FF5C00]">Devis en ligne</span>
        </div>
        <h1 className="text-5xl sm:text-6xl font-black tracking-tighter text-white mb-4">
          Obtenir un devis<br />gratuit
        </h1>
        <p className="text-lg text-white/60 max-w-xl">
          Sélectionnez votre appareil et modèle — le tarif s'affiche instantanément.
        </p>
      </section>

      <section className="pb-24 max-w-4xl mx-auto px-6">
        <div className="grid lg:grid-cols-3 gap-8 items-start">

          {/* ── Form ── */}
          <div className="lg:col-span-2">
            {status === "success" ? (
              <div data-testid="devis-success" className="p-10 rounded-2xl border border-[#34C759]/30 bg-[#34C759]/5 text-center">
                <div className="w-16 h-16 rounded-full bg-[#34C759]/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-[#34C759]" />
                </div>
                <h3 className="text-xl font-black text-white mb-2">Devis envoyé !</h3>
                <p className="text-white/50 text-sm">
                  Nous analyserons votre demande et vous contacterons sous 24h avec une estimation personnalisée.
                </p>
                <button onClick={() => setStatus(null)} className="mt-6 text-sm text-[#FF5C00] hover:underline">
                  Faire une autre demande
                </button>
              </div>
            ) : (
              <form data-testid="devis-form" onSubmit={handleSubmit} className="space-y-7">

                {/* Coordonnées */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-white/30 mb-4">Vos coordonnées</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Nom complet *</label>
                      <input data-testid="devis-input-nom" type="text" name="nom" value={form.nom} onChange={handleChange} required placeholder="Jean Dupont" className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Email *</label>
                      <input data-testid="devis-input-email" type="email" name="email" value={form.email} onChange={handleChange} required placeholder="jean@email.com" className={inputCls} />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className={labelCls}>Téléphone</label>
                    <input data-testid="devis-input-telephone" type="tel" name="telephone" value={form.telephone} onChange={handleChange} placeholder="06 12 34 56 78" className={inputCls} />
                  </div>
                </div>

                {/* Appareil + modèle */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-white/30 mb-4">Votre appareil</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelCls}>Type d'appareil *</label>
                      <select data-testid="devis-input-appareil" name="appareil" value={form.appareil} onChange={handleChange} required className={inputCls}>
                        {appareils.map((a) => (
                          <option key={a} value={a} style={{ background: "#1A1A1E" }}>{a}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Type de réparation *</label>
                      <select data-testid="devis-input-reparation" name="type_reparation" value={form.type_reparation} onChange={handleChange} required className={inputCls}>
                        {(reparations[form.appareil] || reparations.Autre).map((r) => (
                          <option key={r} value={r} style={{ background: "#1A1A1E" }}>{r}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Model select (dynamic) or text fallback */}
                  <div className="mt-4">
                    <label className={labelCls}>
                      Modèle précis
                      {hasModels && (
                        <span className="ml-2 text-[#FF5C00] font-semibold">— prix affiché instantanément</span>
                      )}
                    </label>
                    {hasModels ? (
                      <select
                        data-testid="devis-input-modele"
                        name="modele"
                        value={form.modele}
                        onChange={handleChange}
                        className={inputCls}
                      >
                        <option value="" style={{ background: "#1A1A1E" }}>-- Sélectionnez votre modèle --</option>
                        {gammes.map((gamme) => (
                          <optgroup key={gamme.name} label={gamme.name} style={{ background: "#1A1A1E" }}>
                            {gamme.models.map((m) => (
                              <option key={m.model} value={m.model} style={{ background: "#1A1A1E" }}>
                                {m.model}
                              </option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                    ) : (
                      <input
                        data-testid="devis-input-modele"
                        type="text"
                        name="modele"
                        value={form.modele}
                        onChange={handleChange}
                        placeholder="Ex: Précisez votre modèle"
                        className={inputCls}
                      />
                    )}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-widest text-white/30 mb-4">Détails</h3>
                  <div>
                    <label className={labelCls}>Description du problème</label>
                    <textarea
                      data-testid="devis-input-description"
                      name="description" value={form.description} onChange={handleChange}
                      rows={4}
                      placeholder="Décrivez le problème : quand est-il apparu, comment se manifeste-t-il..."
                      className={`${inputCls} resize-none`}
                    />
                  </div>
                  <div className="mt-4">
                    <label className={labelCls}>Disponibilités souhaitées</label>
                    <input
                      data-testid="devis-input-disponibilite"
                      type="text" name="disponibilite" value={form.disponibilite} onChange={handleChange}
                      placeholder="Ex: Semaine prochaine, mardi ou jeudi matin"
                      className={inputCls}
                    />
                  </div>
                </div>

                {status === "error" && (
                  <p data-testid="devis-error" className="text-sm text-red-400">
                    Une erreur s'est produite. Veuillez réessayer.
                  </p>
                )}
                {status === "captcha" && (
                  <p className="text-sm text-yellow-400">Veuillez compléter le CAPTCHA.</p>
                )}

                <div>
                  <label className={`${labelCls} uppercase tracking-widest`}>Vérification anti-robot *</label>
                  <HCaptchaField captchaRef={captchaRef} onVerify={setCaptchaToken} onExpire={() => setCaptchaToken("")} />
                </div>

                <button
                  data-testid="devis-submit-btn"
                  type="submit"
                  disabled={status === "loading"}
                  className="w-full flex items-center justify-center gap-2 bg-[#FF5C00] hover:bg-[#E05200] disabled:opacity-60 text-white font-semibold py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5"
                >
                  {status === "loading" ? (
                    <span className="animate-pulse">Envoi en cours...</span>
                  ) : (
                    <><Send className="w-4 h-4" />Envoyer ma demande de devis</>
                  )}
                </button>
              </form>
            )}
          </div>

          {/* ── Sidebar ── */}
          <div className="space-y-5 lg:sticky lg:top-24">
            {/* Live estimation */}
            <PriceEstimation estimation={estimation} />

            {/* Inclus */}
            <div className="p-5 rounded-2xl border border-white/5 bg-[#121215]">
              <h3 className="text-sm font-bold text-white mb-4">Inclus dans le devis</h3>
              <ul className="space-y-3">
                {["Estimation précise du coût", "Délai de réparation estimé", "Type de pièces utilisées", "Garantie incluse", "Zéro engagement"].map((item) => (
                  <li key={item} className="flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 text-[#FF5C00] flex-shrink-0" />
                    <span className="text-sm text-white/60">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-5 rounded-2xl border border-[#FF5C00]/20 bg-[#FF5C00]/5">
              <p className="text-sm font-bold text-white mb-1">Diagnostic gratuit</p>
              <p className="text-xs text-white/50 leading-relaxed">
                Vous pouvez aussi venir directement en atelier pour un diagnostic gratuit et sans engagement, du lundi au samedi.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
